import type { BodyHandle, ShapeHandle, ShapeIdentity, World } from 'box3d-web';
import { COTTAGE_PIECES } from '../../structures/cottage/cottageSpec';
import { createCottageRuntimeFromWorld, disposeCottage, drainCottageCanonicalEvents, drainCottageEvents, getCottageSnapshot, impactCottage, readCottageTransforms, resetCottage, type CottageDamageFactEvent, type CottageDirection, type CottageEvent, type CottageImpactBurstEvent, type CottageRuntime, type CottageWeapon } from '../../structures/cottage/cottagePhysics';
import { createDestructionIdentityFactory, type StructureIdentity, type WorldIdentity } from '../identities';
import { DestructionStructureHost, SharedDestructionWorld } from '../sharedDestructionWorld';
import type { DestructionSceneParticipant, SceneCommandEnvelope, SceneCommandPolicy, SceneContactBatch } from '../destructionSceneParticipant';
import { createStructureDamageContext, type StructureDamageContext } from '../damageContext';

export type CottageCanonicalPresentationEvent =
  | CottageImpactBurstEvent
  | (CottageDamageFactEvent & { readonly context: StructureDamageContext });

export interface CottageStructureAdapterOptions { shared: SharedDestructionWorld; ids: ReturnType<typeof createDestructionIdentityFactory>; worldIdentity: WorldIdentity; ordinal?: number; groundBody: BodyHandle; worldOffset?: CottageDirection }

const COTTAGE_WEAPONS = new Set<CottageWeapon>(['cannon', 'spike', 'rubble', 'concussion', 'bomb']);

function checkedPoint(value: unknown, name: string): Readonly<CottageDirection> {
  const point = value as Partial<CottageDirection> | null;
  if (!point || ![point.x, point.y, point.z].every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate))) throw new RangeError(`${name} must contain finite coordinates`);
  return Object.freeze({ x: point.x!, y: point.y!, z: point.z! });
}

function checkedWeapon(value: unknown): CottageWeapon {
  if (typeof value !== 'string' || !COTTAGE_WEAPONS.has(value as CottageWeapon)) throw new RangeError('unsupported cottage weapon');
  return value as CottageWeapon;
}

function checkedPower(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0.1 || value > 3) throw new RangeError('cottage weapon power must be between 0.1 and 3');
  return value;
}

/** Hosted cottage participant: native bodies stay in the shared world, while authored commands remain cottage-domain data. */
export class CottageStructureAdapter implements DestructionSceneParticipant<CottageEvent> {
  readonly structureKey: string;
  readonly physicsWorld: World;
  readonly fixedDt = 1 / 60;
  readonly substeps = 4;
  readonly cottageRuntime: CottageRuntime;
  private readonly shared: SharedDestructionWorld;
  private readonly host: DestructionStructureHost;
  private readonly structureId: StructureIdentity;
  private readonly ids: CottageStructureAdapterOptions['ids'];
  private disposed = false;
  private sceneOwner: object | null = null;
  private tool: CottageWeapon = 'cannon';
  private power = 1;

  constructor(options: CottageStructureAdapterOptions) {
    this.shared = options.shared;
    this.physicsWorld = options.shared.world;
    this.structureId = options.ids.structure(options.ordinal ?? 0, options.worldIdentity);
    this.ids = options.ids;
    this.structureKey = this.structureId.key;
    this.host = options.shared.createStructureHost(this.structureId);
    const ownership = {
      registerBody: (body: BodyHandle, index: number) => this.host.registerBody(body, {
        scope: 'structure', worldId: options.worldIdentity, structureId: this.structureId,
        elementId: options.ids.element(index, this.structureId),
        layerId: options.ids.layer(index, options.ids.element(index, this.structureId)),
        cellId: options.ids.cell(index, options.ids.layer(index, options.ids.element(index, this.structureId))),
        materialId: COTTAGE_PIECES[index].material,
      }),
      registerShape: (shape: number, body: BodyHandle) => this.host.registerShape(shape as ShapeHandle, body),
      unregisterBody: (body: BodyHandle) => this.host.unregisterBody(body),
    };
    try {
      this.cottageRuntime = createCottageRuntimeFromWorld({ world: this.physicsWorld, groundBody: options.groundBody, ownership, ownsWorld: false, worldOffset: options.worldOffset });
    } catch (error) {
      try { this.host.dispose(); } catch { /* preserve construction error */ }
      throw error;
    }
  }

  get revision(): number { return this.host.revision; }
  get isDisposed(): boolean { return this.disposed; }
  get worldOffset(): Readonly<CottageDirection> { return this.cottageRuntime.worldOffset; }
  get hostSnapshot() { return this.host.snapshot(); }
  attachSceneOwner(owner: object): void { this.assertLive(); if (this.sceneOwner && this.sceneOwner !== owner) throw new Error('cottage adapter already attached'); this.sceneOwner = owner; }
  detachSceneOwner(owner: object): void { if (this.sceneOwner === owner) this.sceneOwner = null; }
  prepareSceneCommand(type: string, payload: unknown): unknown {
    const input = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
    if (type === 'fire') return Object.freeze({
      target: checkedPoint(input.target, 'fire.target'),
      direction: checkedPoint(input.direction ?? { x: 0, y: 1, z: 0 }, 'fire.direction'),
      tool: checkedWeapon(input.tool ?? this.tool),
      power: checkedPower(input.power ?? this.power),
    });
    if (type === 'set-tool') return Object.freeze({ tool: checkedWeapon(input.tool) });
    if (type === 'set-weapon-power') return Object.freeze({ power: checkedPower(input.power) });
    if (type === 'reset-structure' || type === 'dispose-structure') return Object.freeze({});
    throw new Error(`unsupported Cottage command ${type}`);
  }
  sceneCommandPolicy(type: string): SceneCommandPolicy { return type === 'set-tool' || type === 'set-weapon-power' ? 'coalescible' : 'critical'; }
  applySceneCommand(owner: object, command: SceneCommandEnvelope): void {
    this.assertOwner(owner);
    const payload = command.payload as Record<string, unknown>;
    if (command.type === 'fire') {
      const target = payload.target as { x: number; y: number; z: number };
      const direction = payload.direction as CottageDirection | undefined;
      impactCottage(this.cottageRuntime, target, direction, payload.tool as CottageWeapon, Number(payload.power));
    } else if (command.type === 'set-tool') this.tool = payload.tool as CottageWeapon;
    else if (command.type === 'set-weapon-power') this.power = Number(payload.power);
    else throw new Error(`unsupported Cottage command ${command.type}`);
  }
  sceneFixedStep(owner: object, batch: SceneContactBatch): void { this.assertOwner(owner); void batch; this.cottageRuntime.simTime += this.fixedDt; readCottageTransforms(this.cottageRuntime); }
  finishSceneFrame(owner: object, delta: number, stepped: boolean): void { this.assertOwner(owner); void delta; void stepped; }
  drainSceneEvents(owner: object): readonly CottageEvent[] { this.assertOwner(owner); return drainCottageEvents(this.cottageRuntime); }
  drainCanonicalEvents(owner: object): readonly CottageCanonicalPresentationEvent[] {
    this.assertOwner(owner);
    const output: CottageCanonicalPresentationEvent[] = [];
    for (const event of drainCottageCanonicalEvents(this.cottageRuntime)) {
      if (event.kind === 'impact-burst') {
        output.push(event);
        continue;
      }
      if (event.pieceIndex < 0) throw new RangeError('canonical Cottage piece index must be non-negative');
      const elementId = this.ids.element(event.pieceIndex, this.structureId);
      const layerId = this.ids.layer(event.pieceIndex, elementId);
      const cellId = this.ids.cell(event.pieceIndex, layerId);
      output.push(Object.freeze({ ...event, context: createStructureDamageContext({ fact: event.payload, worldId: this.structureId.parent as WorldIdentity, structureId: this.structureId, elementId, layerId, cellId, materialId: event.material, weapon: event.weapon }) }));
    }
    return Object.freeze(output);
  }
  sceneReset(owner: object): void { this.assertOwner(owner); resetCottage(this.cottageRuntime); this.host.advanceRevision(); }
  sceneResetClock(owner: object): void { this.assertOwner(owner); this.cottageRuntime.accumulator = 0; }
  ownsProjectileBody(): boolean { return false; }
  resolveShape(identity: ShapeIdentity): boolean {
    const registration = this.shared.registry.resolveShape(identity)?.bodyRegistration;
    return registration?.address.scope === 'structure' && registration.address.structureId.key === this.structureKey;
  }
  snapshot() {
    this.assertLive();
    const structure = this.host.snapshot();
    const cottage = getCottageSnapshot(this.cottageRuntime);
    return {
      structure,
      cottage: Object.freeze({ ...cottage, nativeBodyCount: structure.bodies.length + 1, nativeShapeCount: structure.shapes.length + 1 }),
    };
  }
  dispose(): void {
    if (this.disposed) return;
    if (this.sceneOwner) throw new Error('cottage adapter is scene-owned; detach first');
    try { disposeCottage(this.cottageRuntime); } finally { try { this.host.dispose(); } finally { this.disposed = true; } }
  }

  private assertLive(): void { if (this.disposed) throw new Error('cottage adapter used after dispose()'); }
  private assertOwner(owner: object): void { this.assertLive(); if (this.sceneOwner !== owner) throw new Error('cottage adapter is not attached to this scene owner'); }
}

export async function createCottageStructureAdapter(options: CottageStructureAdapterOptions): Promise<CottageStructureAdapter> { return new CottageStructureAdapter(options); }
